import { observable } from "mobx";

import { isDefined, unique } from "@bps/common-utils";
import { DateTime } from "@bps/date-time";
import { flatten, FontWeights, IColumn } from "@bps/fluent-ui";
import {
  ClinicalDataType,
  DASHModuleTypes,
  EncounterClinicalDataDto,
  ObservationDto,
  ObservationType,
  PSFSMultipleQuestionnaireResponseDto,
  PSFSQuestionnaireResponseDto,
  QuestionnaireDto,
  QuestionnaireResponseDto
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { ClinicalDataToObservationConverter } from "@modules/clinical/screens/patient-record/components/clinical-tools/utils/ClinicalDataToObservationConverter.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { EncounterClinicalData } from "@stores/clinical/models/EncounterClinicalData.ts";
import { Observation } from "@stores/clinical/models/Observation.ts";

import { SeriesQuestionAnswers } from "../../clinical-tools/psfs/PSFSTrendDialog.types.ts";
import {
  createDynamicPsfsAnswerObject,
  getAverages
} from "../../clinical-tools/psfs/utils.ts";

export const SERIES_PREFIX = "series";
export class OutcomeMeasuresCardModel {
  private converter = new ClinicalDataToObservationConverter();
  constructor(private clinicalRecord: ClinicalRecord) {}

  @observable
  filledTools: ClinicalDataType[] = [];
  getComponentData = async (type: ClinicalDataType) => {
    const encounterId = this.clinicalRecord.openEncounter?.id;
    let questionnaire: QuestionnaireDto | undefined;
    let observation: ObservationDto | undefined;
    let data: QuestionnaireResponseDto | undefined;

    if (encounterId) {
      if (type === ClinicalDataType.PSFS || type === ClinicalDataType.NPRS) {
        questionnaire = await this.clinicalRecord.clinical.getQuestionnaires(
          this.converter.clinicalToQuestionnaire(type)
        );
      }

      const result = await this.clinicalRecord.clinical.getObservations({
        patientId: this.clinicalRecord.patient?.id,
        encounterIds: [encounterId],
        types: [this.converter.clinicalToObservation(type)]
      });

      const observations = result.results;

      if (observations && observations.length > 0) {
        switch (type) {
          case ClinicalDataType.PSFS:
            const psfsData = this.clinicalRecord.clinicalData?.psfs;

            if (psfsData && psfsData.responses.length > 0) {
              observation = observations.find(
                m => m.values[0].contextId === psfsData.responses[0].contextId
              );
              data = psfsData.responses[0];
            }
            break;
          case ClinicalDataType.NPRS:
            observation = observations[0];
            data = this.clinicalRecord.clinicalData
              ? (this.clinicalRecord.clinicalData[
                  this.converter
                    .clinicalToObservation(type)
                    .toLocaleLowerCase() as keyof EncounterClinicalData
                ] as QuestionnaireResponseDto)
              : undefined;
            break;
          case ClinicalDataType.DASH:
            if (observations && observations.length > 0) {
              observation = observations[0];
            }
            break;
          default:
            observation = observations[0];
            break;
        }
      }
    }

    return { questionnaire, observation, data };
  };

  predicateDates = (a: ObservationDto, b: ObservationDto) =>
    a.changeLog?.createdDate &&
    b.changeLog?.createdDate &&
    a.changeLog?.createdDate < b.changeLog?.createdDate
      ? 1
      : -1;

  // need this to return the columns and answers array for the table
  getTrendData = async (type: ClinicalDataType, episodeOfCareId?: string) => {
    let questionnaire: QuestionnaireDto | undefined;
    let responses: PSFSQuestionnaireResponseDto[] = [];
    const encounterId =
      this.clinicalRecord.openEncounter?.episodeOfCareId === episodeOfCareId
        ? this.clinicalRecord.openEncounter?.id
        : undefined;

    const encounterResponse =
      (await this.clinicalRecord.clinical.getEncounters({
        episodeOfCareIds: episodeOfCareId ? [episodeOfCareId] : undefined,
        patientId: this.clinicalRecord.id
      })) ?? [];

    const eocEncounterIds = this.clinicalRecord
      .filterEncountersOnCurrentRole(encounterResponse)
      .map(e => e.id);

    let answersArray: SeriesQuestionAnswers[] = [];
    let arrayLength: number = 0;
    const columns: IColumn[] = [];

    if (type === ClinicalDataType.PSFS || type === ClinicalDataType.NPRS) {
      questionnaire = await this.clinicalRecord.clinical.getQuestionnaires(
        this.converter.clinicalToQuestionnaire(type)
      );
    }

    const result = await this.clinicalRecord.clinical.getObservations({
      patientId: this.clinicalRecord.patient?.id,
      types: [this.converter.clinicalToObservation(type)]
    });

    const allObservations = result.results;

    const observation = allObservations.find(
      x => x.encounterId === encounterId
    );

    const linkedObservations = allObservations.filter(x =>
      eocEncounterIds.includes(x.encounterId)
    );

    const contextId = linkedObservations
      ? linkedObservations[0]?.values[0].contextId
      : undefined;

    arrayLength = linkedObservations.length;

    const linkedEncounterIds = unique(
      linkedObservations.map(x => x.encounterId)
    );

    if (allObservations && allObservations.length > 0) {
      switch (type) {
        case ClinicalDataType.PSFS:
          linkedObservations.sort(this.predicateDates);

          // last element is the oldest Observation in the series we always want to show the original series even if a new PSFS is created
          const lastObservation = linkedObservations.pop();
          if (lastObservation) {
            const contextId = lastObservation.values[0].contextId;
            const psfsSeriesContext =
              this.clinicalRecord.clinicalData?.psfsContext?.contexts.find(
                c => c.contextId === contextId
              );

            if (
              contextId &&
              linkedEncounterIds &&
              psfsSeriesContext &&
              questionnaire
            ) {
              const promises = linkedEncounterIds.map(x =>
                this.getEncounterPSFSData(x, contextId)
              );

              const respond = await Promise.all(promises);
              responses = flatten(respond.filter(isDefined));

              responses.sort((a, b) => {
                return a.createLog?.createdDateTime! <
                  b.createLog?.createdDateTime!
                  ? 1
                  : -1;
              });

              const questionIds = psfsSeriesContext.activities.map(
                x => x.questionnaireItemId
              );
              answersArray = createDynamicPsfsAnswerObject({
                questionnaire,
                responses,
                questionIds,
                psfsSeriesContext
              });

              const summaryRow = getAverages(responses, questionIds.length);
              answersArray.push(summaryRow);
              arrayLength = responses.length;
            }
          }
          break;

        case ClinicalDataType.DASH:
          [answersArray, arrayLength] = this.createDashDynamicAnswerObject(
            linkedObservations,
            linkedEncounterIds
          );
          break;

        default:
          answersArray = this.createDynamicAnswerObject(linkedObservations);
          break;
      }

      for (let x = 0; x < arrayLength; x++) {
        const name = this.recordDateString(
          x,
          {
            responses,
            linkedObservations
          },
          encounterId
        );

        const column = this.getColumn(x, arrayLength, name);
        columns.push(column);
      }

      const activitiesColumn: IColumn = this.getActiveColumn(arrayLength);
      columns.unshift(activitiesColumn);
    }

    return { questionnaire, answersArray, columns, observation, contextId };
  };

  recordDateString = (
    key: number,
    arrays: {
      responses: PSFSQuestionnaireResponseDto[];
      linkedObservations: Observation[];
    },
    encounterId?: string
  ) => {
    const { responses, linkedObservations } = arrays;

    const stringDate =
      responses && responses.length > 0
        ? responses[key].createLog?.createdDateTime
        : linkedObservations && linkedObservations[key].changeLog?.createdDate;

    const actualDate = DateTime.fromISO(stringDate)?.toDayDefaultFormat();

    const thisEncounterId =
      responses && responses.length > 0
        ? responses[key].createLog?.createdEncounterId
        : linkedObservations && linkedObservations[key].encounterId;

    const isCurrentEncounter = encounterId && thisEncounterId === encounterId;
    const today = DateTime.now().toDayDefaultFormat();

    if (actualDate) {
      const isToday = actualDate === today;
      return isToday && isCurrentEncounter ? "Today" : actualDate;
    }
    return "";
  };

  getActiveColumn = (arrayLength: number): IColumn => {
    const SMALLEST_DESC_WIDTH = 128;
    const descColWidth = this.descriptionColumnWidth(
      arrayLength,
      95,
      SMALLEST_DESC_WIDTH
    );
    return {
      key: "Description",
      isRowHeader: true,
      minWidth: SMALLEST_DESC_WIDTH,
      maxWidth: descColWidth,
      name: "Description",
      onRender: (item: SeriesQuestionAnswers) => {
        return `${item.questionNumberText ?? ""} ${item.questionText}`;
      },
      isMultiline: true
    };
  };

  getColumn = (key: number, arrayLength: number, name: string): IColumn => {
    const fontWeight =
      key === arrayLength - 1 ? FontWeights.bold : FontWeights.regular;

    return {
      key: String(key),
      isRowHeader: true,
      minWidth: 75,
      maxWidth: 75,
      name,
      styles: { root: { fontWeight } },
      onRender: (item: SeriesQuestionAnswers) => {
        const answerKey = `${SERIES_PREFIX}${key}`;
        return item[answerKey] ?? "";
      },
      isMultiline: false
    };
  };

  descriptionColumnWidth = (
    arrayLength: number,
    columnWidth: number,
    smallestDescriptionWidth: number
  ) => {
    if (arrayLength >= 4) return smallestDescriptionWidth;
    if (arrayLength === 3) return smallestDescriptionWidth + columnWidth;
    if (arrayLength === 2) return smallestDescriptionWidth + columnWidth * 2;
    return smallestDescriptionWidth + columnWidth * 3;
  };

  getEncounterPSFSData = async (encounterId: string, contextId: string) => {
    const encounterClincialData =
      await this.clinicalRecord.clinical.getEncounterClinicalData(
        {
          encounterId
        },
        { ignoreCache: true }
      );

    const data = encounterClincialData[
      ObservationType.PSFS.toLocaleLowerCase() as keyof EncounterClinicalDataDto
    ] as PSFSMultipleQuestionnaireResponseDto;

    if (data && data.responses) {
      return data.responses.filter(r => r.contextId === contextId);
    }
    return undefined;
  };

  createDynamicAnswerObject = (
    linkedObservations: ObservationDto[] | undefined
  ) => {
    if (linkedObservations && linkedObservations.length > 0) {
      const observations = linkedObservations.slice().sort(this.predicateDates);

      const questionAnswers: SeriesQuestionAnswers = {
        questionId: "q1",
        questionText: "Score"
      };

      return this.getAnswersArray(observations, questionAnswers);
    }
    return [];
  };

  getAnswersArray = (
    observations: ObservationDto[] | ObservationDto[][],
    questionAnswers: SeriesQuestionAnswers,
    predicate?: (dto: ObservationDto) => boolean
  ) => {
    const answersArray: SeriesQuestionAnswers[] = [];
    let containValue: boolean = false;

    observations.forEach(
      (item: ObservationDto | ObservationDto[], idx: number) => {
        const answerKey = `${SERIES_PREFIX}${idx}`;

        const searchedItem =
          Array.isArray(item) && predicate
            ? (item as ObservationDto[]).find(predicate)
            : (item as ObservationDto);

        const answer = searchedItem && (searchedItem?.values[0].value ?? 0);

        if (answer !== undefined) {
          questionAnswers[answerKey] = answer;
          containValue = true;
        }
      }
    );
    if (containValue) {
      answersArray.push(questionAnswers);
    }

    return answersArray;
  };

  createDashDynamicAnswerObject = (
    linkedObservations: ObservationDto[] | undefined,
    linkedEncounterIds: string[]
  ): [SeriesQuestionAnswers[], number] => {
    // need to create special array object of linked Observations get all Observations for each encounter and add to an array then add all those ar
    const dashType = [
      DASHModuleTypes.Disability,
      DASHModuleTypes.Work,
      DASHModuleTypes.Sport
    ];

    if (linkedObservations && linkedObservations.length > 0) {
      let answersArray: SeriesQuestionAnswers[] = [];

      const dashObservationsGrouped: Array<Array<ObservationDto>> =
        linkedEncounterIds
          .map(encounterId => {
            return linkedObservations.filter(
              x => x.encounterId === encounterId
            );
          })
          .sort((a, b) => this.predicateDates(a[0], b[0]));

      dashType.forEach((questionText, idx) => {
        const questionAnswers: SeriesQuestionAnswers = {
          questionId: `q${idx}`,
          questionText
        };

        answersArray = [
          ...answersArray,
          ...this.getAnswersArray(
            dashObservationsGrouped,
            questionAnswers,
            y => y.summary === questionText
          )
        ];
      });

      return [answersArray, dashObservationsGrouped.length];
    }

    return [[], 0];
  };
}
